import { ReactNode, useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useMe } from "../api/hooks/useMe";
import { Spinner } from "../ui/Spinner";
import { getLocale, setLocale, LOCALES, type LocaleId } from "../i18n";

// Gates the entire authenticated app tree. Children only mount after
// /api/me has resolved with a real user; null result → redirect to
// /sign-in?next=<current>. Loading shows a centered spinner rather than
// a flash of redirect — important because a logged-in user reloading a
// deep link should not see the sign-in page even for a frame.

export function AuthGate({ children }: { readonly children: ReactNode }){
  const me = useMe();
  const location = useLocation();

  // Cross-device locale sync: once /me resolves, apply the user's persisted
  // preferredLocale (a fresh device has no localStorage yet, so this is what
  // makes the choice follow them). No-op when it already matches the live
  // locale (the picker keeps localStorage + the DB in step).
  const pref = me.data?.preferredLocale;
  useEffect(() => {
    if (pref && LOCALES.some((l) => l.id === pref) && pref !== getLocale()) {
      setLocale(pref as LocaleId);
    }
  }, [pref]);

  // Trigger a refetch when the apiFetch 401 handler invalidates ["me"]
  // — handled in router.tsx by wiring setUnauthorizedHandler to
  // queryClient.setQueryData(ME_KEY, null).
  useEffect(() => {
    if (me.error && me.error.status !== 401) {
      // surface unexpected /me errors so we don't silently strand the user
      console.error("AuthGate /me error", me.error);
    }
  }, [me.error]);

  if (me.isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Spinner />
      </div>
    );
  }
  if (!me.data) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/sign-in?next=${next}`} replace />;
  }
  return <>{children}</>;
}
